import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Check, WarningCircle } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignContractPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signed, setSigned] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/contracts/sign/${token}`);
        const data = await res.json();
        if (!res.ok) { setError(data.detail || "Invalid link"); return; }
        if (data.already_signed) { setSigned(true); setContract(data); return; }
        setContract(data);
      } catch { setError("Failed to load contract"); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handleSign = async () => {
    if (!signerName.trim()) { toast.error("Please enter your name"); return; }
    if (!agreed) { toast.error("Please agree to the terms"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contracts/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signer_name: signerName }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Failed to sign"); return; }
      setSigned(true);
      toast.success("Contract signed successfully");
    } catch { toast.error("Failed to sign contract"); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md w-full">
        <WarningCircle size={48} className="text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-[#0F172A] mb-2">Contract Unavailable</h2>
        <p className="text-sm text-[#6B7280]">{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md w-full" data-testid="contract-signed-confirmation">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-green-600" weight="bold" />
        </div>
        <h2 className="text-lg font-bold text-[#0F172A] mb-2">Contract Signed</h2>
        <p className="text-sm text-[#6B7280]">
          {contract?.signed_by_name
            ? `Signed by ${contract.signed_by_name} on ${new Date(contract.signed_at).toLocaleString()}`
            : "The agreement has been signed successfully. Thank you."}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4" data-testid="sign-contract-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-[#0F172A] rounded-t-xl p-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-3" style={{ backgroundColor: "#C5A059" }}>
            <span className="text-white font-bold text-lg">BM</span>
          </div>
          <h1 className="text-xl font-bold text-white">Banks Morgan Recruitment</h1>
          <p className="text-xs text-[#94A3B8] mt-1">Recruitment Services Agreement</p>
        </div>

        {/* Contract Summary */}
        <div className="bg-white rounded-b-xl shadow-lg p-6 space-y-6">
          <div>
            <p className="text-xs text-[#6B7280] uppercase font-medium">Contract Reference</p>
            <p className="text-sm font-bold text-[#0F172A]">{contract?.contract_number}</p>
          </div>

          <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-[#0F172A]">Agreement Summary</h3>
            {[
              ["Position", contract?.vacancy_title],
              ["Client", contract?.company_name],
              ["Recruitment Fee", `${contract?.fee_percentage}% of annual salary`],
              ["Estimated Fee", `\u00a3${contract?.fee_amount?.toLocaleString()} + VAT`],
              ["Guarantee Period", contract?.guarantee_days > 0 ? `${contract?.guarantee_days} days` : "No Guarantee"],
              ["Payment Terms", contract?.payment_terms],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-1">
                <span className="text-[#6B7280]">{k}</span>
                <span className="font-medium text-[#0F172A] text-right">{v}</span>
              </div>
            ))}
          </div>

          {contract?.special_terms && (
            <div>
              <p className="text-xs font-semibold text-[#6B7280] uppercase mb-1">Special Terms</p>
              <p className="text-sm text-[#0F172A] bg-amber-50 border border-amber-200 p-3 rounded">{contract.special_terms}</p>
            </div>
          )}

          <div className="border-t pt-4 space-y-4">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#C5A059]" /> Electronic Signature
            </h3>
            <Input
              placeholder="Full Name (as signature) *"
              value={signerName}
              onChange={e => setSignerName(e.target.value)}
              className="text-base"
              data-testid="signer-name-input"
            />
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-1 accent-[#C5A059]"
                data-testid="agree-terms-checkbox"
              />
              <span className="text-xs text-[#6B7280] leading-relaxed">
                I confirm that I have read and agree to the terms of this Recruitment Services Agreement,
                including the fee structure, guarantee period, and payment terms outlined above.
                I understand this constitutes a legally binding electronic signature.
              </span>
            </label>
            <Button
              onClick={handleSign}
              disabled={submitting || !signerName.trim() || !agreed}
              className="w-full bg-[#C5A059] hover:bg-[#B8933F] text-white h-12 text-base"
              data-testid="sign-contract-btn"
            >
              {submitting ? "Signing..." : "Sign Agreement"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
